import { getIsHeavyAnimating, onFullyIdle } from '../lib/teact/teact';
import { addCallback, removeCallback } from '../lib/teact/teactn';

import type {
  ApiAvailableReaction,
  ApiMessage,
  ApiSticker,
  ApiStickerSet,
  ApiThumbnail,
} from '../api/types';
import type {
  MessageList, ThreadId, ThreadLocalState, TopicsInfo,
} from '../types';
import type { ActionReturnType, GlobalState, SharedState } from './types';
import { ApiMessageEntityTypes, MAIN_THREAD_ID } from '../api/types';

import {
  ALL_FOLDER_ID, ANIMATION_LEVEL_DEFAULT,
  ARCHIVED_FOLDER_ID,
  DEBUG,
  FOLDERS_POSITION_DEFAULT,
  GLOBAL_STATE_CACHE_ARCHIVED_CHAT_LIST_LIMIT,
  GLOBAL_STATE_CACHE_CHAT_LIST_LIMIT,
  GLOBAL_STATE_CACHE_CUSTOM_EMOJI_LIMIT,
  GLOBAL_STATE_CACHE_DISABLED,
  GLOBAL_STATE_CACHE_STICKER_SET_LIMIT,
  GLOBAL_STATE_CACHE_USER_LIST_LIMIT,
  IS_SCREEN_LOCKED_CACHE_KEY,
  MESSAGE_STORE_CHAT_LIMIT,
  MESSAGE_STORE_MESSAGE_LIMIT,
  SAVED_FOLDER_ID,
  SHARED_STATE_CACHE_KEY,
} from '../config';
import { MAIN_IDB_STORE } from '../util/browser/idb';
import { isUserId } from '../util/entities/ids';
import { getOrderedIds } from '../util/folderManager';
import {
  compact, omit, pick, pickTruthy, unique,
} from '../util/iteratees';
import { clearMessageStore, pruneMessageStore, saveChatMessages } from '../util/messageStore';
import { ACCOUNT_SLOT, GLOBAL_STATE_CACHE_KEY } from '../util/multiaccount';
import { encryptSession } from '../util/passcode';
import { onBeforeUnload, throttle } from '../util/schedulers';
import { clearStoredSession, hasStoredSession } from '../util/sessions';
import { hasPersistedSession } from '../api/rest/session';
import { selectThreadInfo } from './selectors/threads';
import { addActionHandler, getGlobal } from './index';
import { INITIAL_GLOBAL_STATE, INITIAL_PERFORMANCE_STATE_MED } from './initialState';
import { clearGlobalForLockScreen, clearSharedStateForLockScreen } from './reducers';
import {
  selectChatLastMessageId,
  selectChatMessages,
  selectCurrentMessageList,
  selectFullWebPageFromMessage,
  selectTopics,
  selectTopicsInfo,
  selectViewportIds,
  selectVisibleUsers,
} from './selectors';

import { getIsMobile } from '../hooks/useAppLayout';

const UPDATE_THROTTLE = 5000;

// Kept apart from `GLOBAL_STATE_CACHE_KEY` (which used to hold a full copy of the global here) so a
// build that predates this one cannot read the reduced payload as if it were a whole state.
const THREAD_STATE_FLUSH_KEY = `${GLOBAL_STATE_CACHE_KEY}-threads`;
const FLUSHED_THREAD_STATE_KEYS = [
  'draft', 'editingId', 'editingScheduledId', 'editingDraft', 'editingScheduledDraft',
  'noWebPage', 'lastScrollOffset', 'lastViewportIds',
] satisfies (keyof ThreadLocalState)[];

type FlushedThreadLocalStates = Record<string, Record<string, Partial<ThreadLocalState>>>;

const updateCacheThrottled = throttle(() => onFullyIdle(() => updateCache()), UPDATE_THROTTLE, false);
const updateCacheForced = () => updateCache(true);

// The `byChatId` entry each chat's persisted history was written from, so an unchanged reference can
// skip the write. Session-only: a reload re-reads the store anyway.
// What the last write for each chat contained: the persisted ids and the source message references
// they were built from. Only the persisted subset is kept (≤ `MESSAGE_STORE_MESSAGE_LIMIT` entries,
// all of them also live in current state), so this never pins an older copy of a chat in memory.
type PersistedMessagesSnapshot = { ids: number[]; byId: Record<number, ApiMessage> };
const lastPersistedByChatId = new Map<string, PersistedMessagesSnapshot>();

function isSamePersistedPayload(
  previous: PersistedMessagesSnapshot, ids: number[], byId: Record<number, ApiMessage>,
) {
  if (previous.ids.length !== ids.length) return false;

  return ids.every((id, index) => previous.ids[index] === id && previous.byId[id] === byId[id]);
}

let isCaching = false;
let isRemovingCache = false;
let cacheUpdateSuspensionTimestamp = 0;
let unsubscribeFromBeforeUnload: NoneToVoidFunction | undefined;

export function cacheGlobal(global: GlobalState) {
  return MAIN_IDB_STORE.set(GLOBAL_STATE_CACHE_KEY, global);
}

export function cacheSharedState(state: SharedState) {
  return MAIN_IDB_STORE.set(SHARED_STATE_CACHE_KEY, state);
}

export function loadCachedGlobal() {
  return MAIN_IDB_STORE.get<GlobalState>(GLOBAL_STATE_CACHE_KEY);
}

export function loadCachedSharedState() {
  return MAIN_IDB_STORE.get<SharedState>(SHARED_STATE_CACHE_KEY);
}

export function removeGlobalFromCache() {
  // Whatever the signature described is no longer on disk, so the next tick must write rather than
  // recognise its own bookkeeping and skip
  lastGlobalSignature = undefined;
  lastSharedState = undefined;
  return MAIN_IDB_STORE.del(GLOBAL_STATE_CACHE_KEY);
}

export function removeSharedStateFromCache() {
  return MAIN_IDB_STORE.del(SHARED_STATE_CACHE_KEY);
}

function cacheIsScreenLocked(global: GlobalState) {
  if (global?.passcode?.isScreenLocked) localStorage.setItem(IS_SCREEN_LOCKED_CACHE_KEY, 'true');
}

export function initCache() {
  if (GLOBAL_STATE_CACHE_DISABLED) {
    return;
  }

  const resetCache = () => {
    isRemovingCache = true;
    lastPersistedByChatId.clear();
    void clearMessageStore();
    removeGlobalFromCache().finally(() => {
      localStorage.removeItem(IS_SCREEN_LOCKED_CACHE_KEY);
      isRemovingCache = false;
      if (!isCaching) {
        return;
      }

      clearCaching();
    });
  };

  addActionHandler('saveSession', (): ActionReturnType => {
    if (isCaching) {
      return;
    }

    setupCaching();
    updateCacheForced();
  });

  addActionHandler('reset', resetCache);
}

export async function loadCache(initialState: GlobalState): Promise<GlobalState | undefined> {
  if (GLOBAL_STATE_CACHE_DISABLED) {
    return undefined;
  }

  const cache = await readCache(initialState);

  // `hasStoredSession()` only checks MTProto localStorage (never set in this REST fork), so also
  // accept a persisted REST session — otherwise the cached global/shared state (theme, settings,
  // chats) is discarded on every reload and caching is never resumed.
  if (cache.passcode.hasPasscode || hasStoredSession() || await hasPersistedSession()) {
    setupCaching();

    return cache;
  } else {
    clearCaching();

    // No valid session at boot (expired/revoked/any error path that dropped the user to login). Purge
    // stale persisted login data so the old account never lingers in the switcher or rehydrates on a
    // later boot. `readCache` already dropped the localStorage global-state cache; this clears the IDB
    // copy plus the `account<slot>` entry, mirroring what logout's `reset` does.
    void removeGlobalFromCache();
    clearStoredSession(ACCOUNT_SLOT);

    return undefined;
  }
}

export function setupCaching() {
  if (isCaching) return; // Idempotent: safe to call from login (onUpdateCurrentUser) and boot alike.
  isCaching = true;
  unsubscribeFromBeforeUnload = onBeforeUnload(updateCacheForced, true);
  window.addEventListener('blur', updateCacheForced);
  // WebKit aborts pending IndexedDB transactions when the page is torn down, so the async cache
  // write on unload is lost and a quick reload drops recent state (e.g. an unsent draft). Mirror the
  // per-thread local state into `localStorage` synchronously on hide/unload — `readCache` merges it
  // over the IDB copy on next boot. `visibilitychange`/`pagehide` fire while the page is still alive
  // on Safari.
  document.addEventListener('visibilitychange', handleVisibilityFlush);
  window.addEventListener('pagehide', flushCacheToStorageSync);
  addCallback(updateCacheThrottled);
}

export function clearCaching() {
  isCaching = false;
  // Nothing was written while caching was off, so the next `setupCaching` must not trust the old
  // signature and skip the first write
  lastGlobalSignature = undefined;
  lastSharedState = undefined;
  removeCallback(updateCacheThrottled);
  window.removeEventListener('blur', updateCacheForced);
  document.removeEventListener('visibilitychange', handleVisibilityFlush);
  window.removeEventListener('pagehide', flushCacheToStorageSync);
  if (unsubscribeFromBeforeUnload) {
    unsubscribeFromBeforeUnload();
  }
}

function handleVisibilityFlush() {
  if (document.visibilityState === 'hidden') {
    flushCacheToStorageSync();
  } else {
    // Back in the foreground: the throttled IDB cache is authoritative again, so drop the hide-time
    // snapshot — otherwise a later reload would restore it and lose edits made after returning.
    localStorage.removeItem(THREAD_STATE_FLUSH_KEY);
  }
}

// Synchronous fallback for page hide/unload: IndexedDB writes are async and WebKit aborts them on
// teardown, but `localStorage` is synchronous and survives. `readCache` merges this over the IDB
// copy and clears it, so it only acts as a last-moment snapshot for the next boot.
//
// Only the per-thread local state is written, not the whole global. Everything else is either
// unchanged since the last throttled IDB write or re-fetched at boot anyway, whereas a draft typed
// in the last few seconds exists nowhere else. Serializing the full global here cost 55 ms of
// synchronous main-thread time on EVERY tab switch (measured against a 899 KB cache), and at ~900 KB
// per account it also overran the 5 MB `localStorage` budget once a few slots were signed in —
// at which point this whole safety net silently stopped working.
export function flushCacheToStorageSync() {
  if (isRemovingCache || !isCaching) return;

  const global = getGlobal();
  // Encrypted (passcode) accounts keep using the existing IDB path — never dump plaintext here.
  if (global.auth.isLoggingOut || global.passcode.hasPasscode) return;

  try {
    localStorage.setItem(THREAD_STATE_FLUSH_KEY, JSON.stringify(reduceThreadLocalStates(global)));
  } catch (err) {
    // Quota exceeded or serialization error — fall back to the throttled IDB cache.
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn(err);
    }
  }
}

// The fields that cannot be recovered from the server or re-derived on scroll: what the user typed,
// what they were editing, and where they were looking. `listedIds`/`outlyingLists`/`pinnedIds` are
// deliberately left out — they are the large ones, and the message list rebuilds them on load.
function reduceThreadLocalStates(global: GlobalState) {
  const byChatId: FlushedThreadLocalStates = {};

  for (const chatId of Object.keys(global.messages.byChatId)) {
    const { threadsById } = global.messages.byChatId[chatId];
    const threads: Record<string, Partial<ThreadLocalState>> = {};

    for (const threadId of Object.keys(threadsById)) {
      const { localState } = threadsById[Number(threadId)];
      if (!localState) continue;

      const reduced = pickTruthy(localState, FLUSHED_THREAD_STATE_KEYS);
      if (Object.keys(reduced).length) {
        threads[threadId] = reduced;
      }
    }

    if (Object.keys(threads).length) {
      byChatId[chatId] = threads;
    }
  }

  return byChatId;
}

// Merge a hide-time snapshot back over the (up to `UPDATE_THROTTLE` stale) IDB copy.
//
// Only chats the cached state already knows about are touched. Creating an entry for an unknown chat
// looks harmless but is not: the rest of the boot assumes every chat in `messages.byChatId` has a
// matching chat record, and seeding an orphan hangs the app on load. The chats this could restore a
// draft for are ones opened within `UPDATE_THROTTLE` of the reload and never cached at all — rare,
// and cheaper to lose than a boot that never finishes.
function applyFlushedThreadLocalStates(cached: GlobalState, flushed: FlushedThreadLocalStates) {
  for (const chatId of Object.keys(flushed)) {
    const chatMessages = cached.messages.byChatId[chatId];
    if (!chatMessages) continue;

    for (const threadId of Object.keys(flushed[chatId])) {
      const thread = chatMessages.threadsById[Number(threadId)];
      if (!thread) continue;

      thread.localState = {
        ...thread.localState,
        ...flushed[chatId][threadId],
      };
    }
  }
}

async function readCache(initialState: GlobalState): Promise<GlobalState> {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.time('global-state-cache-read');
  }

  // Left behind by builds that flushed the whole global here; the reduced snapshot now has its own
  // key. Drop it so it cannot linger as ~900 KB of dead weight in a 5 MB budget.
  localStorage.removeItem(GLOBAL_STATE_CACHE_KEY);

  const flushedJson = localStorage.getItem(THREAD_STATE_FLUSH_KEY);
  localStorage.removeItem(THREAD_STATE_FLUSH_KEY);

  let cached = await loadCachedGlobal();

  const cachedSharedState = await loadCachedSharedState();
  const sharedState = cachedSharedState || initialState.sharedState;

  if (cached) {
    cached = {
      ...cached,
      sharedState,
    };
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.timeEnd('global-state-cache-read');
  }

  if (cached) {
    migrateCache(cached, initialState);
  }

  // Applied after the migration, never before: several migration steps replace `messages` wholesale,
  // which would silently throw the restored drafts away again.
  if (cached && flushedJson) {
    try {
      applyFlushedThreadLocalStates(cached, JSON.parse(flushedJson) as FlushedThreadLocalStates);
    } catch (err) {
      // A malformed snapshot must not cost the user the whole cached state
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn(err);
      }
    }
  }

  if (cached) {
    clearStaleNotJoinedFlags(cached);
    clearStaleCallFlags(cached);
  }

  const newState: GlobalState = {
    ...initialState,
    ...cached,
    sharedState: {
      ...sharedState,
      ...cached?.sharedState, // Allow migration to override shared state
    },
  };

  return newState;
}

// `isNotJoined` on a chat that is still in a chat list is a contradiction: leaving a chat drops it
// from `listIds` in the same step that raises the flag, so the two can only disagree if a stray
// membership update raised the flag on its own. That state is what makes a chat vanish from the
// list — and because it is cached, it used to outlive every reload, leaving clearing site data as
// the only way back. Boot from the consistent reading instead: the chat list is the record of what
// the user is in, and the next fetch re-raises the flag if the chat really is gone.
function clearStaleNotJoinedFlags(cached: GlobalState) {
  const listedIds = new Set(Object.values(cached.chats?.listIds || {}).flatMap((ids) => ids || []));
  if (!listedIds.size) return;

  for (const chatId of listedIds) {
    const chat = cached.chats.byId[chatId];
    if (chat?.isNotJoined) {
      cached.chats.byId[chatId] = { ...chat, isNotJoined: undefined };
    }
  }
}

// A group call is live state: it exists while the call does and ends with it. Nothing rebuilds these
// two flags from the server — only the `updateGroupCallChatId` update sets them — so a client that
// left a call before it was discarded never hears the news and keeps `isCallActive` forever. Cached,
// that survived every reload and left the chat offering a "Join video chat" that did nothing.
// `reduceChats` no longer writes the flags; this clears the ones older builds already stored.
function clearStaleCallFlags(cached: GlobalState) {
  for (const [chatId, chat] of Object.entries(cached.chats?.byId || {})) {
    if (chat?.isCallActive || chat?.isCallNotEmpty) {
      cached.chats.byId[chatId] = { ...chat, isCallActive: undefined, isCallNotEmpty: undefined };
    }
  }
}

export function migrateCache(cached: GlobalState, initialState: GlobalState) {
  try {
    unsafeMigrateCache(cached, initialState);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function unsafeMigrateCache(cached: GlobalState, initialState: GlobalState) {
  const untypedCached = cached as any;
  // Pre-fill settings with defaults
  cached.settings.byKey = {
    ...initialState.settings.byKey,
    ...cached.settings.byKey,
  };

  cached.chatFolders = {
    ...initialState.chatFolders,
    ...cached.chatFolders,
  };

  if (!cached.chats.similarChannelsById) {
    cached.chats.similarChannelsById = initialState.chats.similarChannelsById;
  }

  if (!cached.chats.similarBotsById) {
    cached.chats.similarBotsById = initialState.chats.similarBotsById;
  }

  if (!cached.chats.lastMessageIds) {
    cached.chats.lastMessageIds = initialState.chats.lastMessageIds;
  }

  if (!cached.downloadedMedia) {
    cached.downloadedMedia = initialState.downloadedMedia;
  }

  // Clear old color storage to optimize cache size
  if (untypedCached?.appConfig.peerColors) {
    untypedCached.appConfig.peerColors = undefined;
    untypedCached.appConfig.darkPeerColors = undefined;
  }

  if (!cached.fileUploads.byMessageKey) {
    cached.fileUploads.byMessageKey = {};
  }

  if (!cached.reactions) {
    cached.reactions = initialState.reactions;
  }

  if (!cached.quickReplies) {
    cached.quickReplies = initialState.quickReplies;
  }

  if (!cached.users.previewMediaByBotId) {
    cached.users.previewMediaByBotId = initialState.users.previewMediaByBotId;
  }
  if (!cached.chats.loadingParameters) {
    cached.chats.loadingParameters = initialState.chats.loadingParameters;
  }
  if (!cached.topPeerCategories) {
    cached.topPeerCategories = initialState.topPeerCategories;
  }

  if (!cached.reactions.defaultTags?.[0]?.type) {
    cached.reactions = initialState.reactions;
  }

  if (!cached.users.commonChatsById) {
    cached.users.commonChatsById = initialState.users.commonChatsById;
  }
  if (!cached.users.botAppPermissionsById) {
    cached.users.botAppPermissionsById = initialState.users.botAppPermissionsById;
  }
  if (!cached.chats.topicsInfoById) {
    cached.chats.topicsInfoById = initialState.chats.topicsInfoById;
  }

  if (!cached.messages.pollById) {
    cached.messages.pollById = initialState.messages.pollById;
  }
  if (!cached.settings.botVerificationShownPeerIds) {
    cached.settings.botVerificationShownPeerIds = initialState.settings.botVerificationShownPeerIds;
  }

  if (!cached.peers) {
    cached.peers = initialState.peers;
  }

  if (!cached.settings.accountDaysTtl) {
    cached.settings.accountDaysTtl = initialState.settings.accountDaysTtl;
  }

  if (!cached.cacheVersion) {
    cached.cacheVersion = initialState.cacheVersion;
    // Reset because of the new action message structure
    cached.messages = initialState.messages;
    cached.chats.listIds = initialState.chats.listIds;
  }

  if (!cached.messages.playbackByChatId) {
    cached.messages.playbackByChatId = initialState.messages.playbackByChatId;
  }

  if (cached.cacheVersion < 2) {
    if (untypedCached.settings.themes.dark) {
      untypedCached.settings.themes.dark.patternColor = (initialState as any).settings.themes.dark!.patternColor;
    }

    if (untypedCached.settings.themes.light) {
      untypedCached.settings.themes.light.patternColor = (initialState as any).settings.themes.light!.patternColor;
    }

    cached.cacheVersion = 2;
  }

  if (!cached.chats.notifyExceptionById) {
    cached.chats.notifyExceptionById = initialState.chats.notifyExceptionById;
  }

  if (!cached.chats.fullInfoById) {
    cached.chats.fullInfoById = initialState.chats.fullInfoById;
  }

  if (!cached.sharedState) {
    cached.sharedState = initialState.sharedState;
    cached.sharedState.settings = {
      canDisplayChatInTitle: untypedCached.settings.byKey.canDisplayChatInTitle,
      animationLevel: untypedCached.settings.byKey.animationLevel,
      foldersPosition: FOLDERS_POSITION_DEFAULT,
      messageSendKeyCombo: untypedCached.settings.byKey.messageSendKeyCombo,
      messageTextSize: untypedCached.settings.byKey.messageTextSize,
      performance: untypedCached.settings.performance,
      theme: untypedCached.settings.byKey.theme,
      timeFormat: untypedCached.settings.byKey.timeFormat,
      wasTimeFormatSetManually: untypedCached.settings.byKey.wasTimeFormatSetManually,
      shouldUseSystemTheme: untypedCached.settings.byKey.shouldUseSystemTheme,
      isConnectionStatusMinimized: untypedCached.settings.byKey.isConnectionStatusMinimized,
      shouldForceHttpTransport: untypedCached.settings.byKey.shouldForceHttpTransport,
      language: untypedCached.settings.byKey.language,
      languages: untypedCached.settings.languages,
      shouldSkipWebAppCloseConfirmation: untypedCached.settings.byKey.shouldSkipWebAppCloseConfirmation,
      miniAppsCachedPosition: untypedCached.settings.miniAppsCachedPosition,
      miniAppsCachedSize: untypedCached.settings.miniAppsCachedSize,
      shouldAllowHttpTransport: untypedCached.settings.byKey.shouldAllowHttpTransport,
      shouldCollectDebugLogs: untypedCached.settings.byKey.shouldCollectDebugLogs,
      shouldDebugExportedSenders: untypedCached.settings.byKey.shouldDebugExportedSenders,
      shouldWarnAboutFiles: untypedCached.settings.byKey.shouldWarnAboutFiles,
    };
  }

  if (!cached.settings.themes) {
    cached.settings.themes = initialState.settings.themes;
  }

  if (!cached.messages.webPageById) {
    cached.messages.webPageById = initialState.messages.webPageById;
  }

  const cachedSharedSettings = cached.sharedState.settings;
  if (!cachedSharedSettings.wasAnimationLevelSetManually) {
    cachedSharedSettings.animationLevel = ANIMATION_LEVEL_DEFAULT;
    cachedSharedSettings.performance = INITIAL_PERFORMANCE_STATE_MED;
  }

  if (cachedSharedSettings.performance.messageBlur === undefined) {
    cachedSharedSettings.performance.messageBlur = false;
  }

  if (cachedSharedSettings.performance.textStreaming === undefined) {
    cachedSharedSettings.performance.textStreaming = true;
  }

  if (!cachedSharedSettings.foldersPosition) {
    cachedSharedSettings.foldersPosition = FOLDERS_POSITION_DEFAULT;
  }

  if (!cached.appConfig) {
    cached.appConfig = initialState.appConfig;
  }

  if (cached.appConfig.webAppAllowedProtocols === undefined) {
    cached.appConfig.webAppAllowedProtocols = initialState.appConfig.webAppAllowedProtocols;
  }

  if (untypedCached.sharedState?.settings?.shouldWarnAboutSvg) {
    cached.sharedState.settings.shouldWarnAboutFiles = true;
    untypedCached.sharedState.settings.shouldWarnAboutSvg = undefined;
  }

  if (cached.cacheVersion < 3) {
    cached.cacheVersion = 3;
    cached.messages = initialState.messages;
    cached.chats.listIds = initialState.chats.listIds;
  }

  if (cached.cacheVersion < 4) {
    cached.cacheVersion = 4;
    // Dev backend: turn the per-message Translate button on for existing cached state (the new default).
    cached.settings.byKey.canTranslate = true;
  }

  if (cached.cacheVersion < 5) {
    cached.cacheVersion = 5;
    // Media no longer loads by itself: existing users start with auto-download off too and can turn it on in Settings
    Object.assign(cached.settings.byKey, {
      canAutoLoadPhotoFromContacts: false,
      canAutoLoadPhotoInPrivateChats: false,
      canAutoLoadPhotoInGroups: false,
      canAutoLoadPhotoInChannels: false,
      canAutoLoadVideoFromContacts: false,
      canAutoLoadVideoInPrivateChats: false,
      canAutoLoadVideoInGroups: false,
      canAutoLoadVideoInChannels: false,
    } satisfies Partial<typeof cached.settings.byKey>);
  }

  if (cached.cacheVersion < 6) {
    cached.cacheVersion = 6;
    // Photos and videos load by themselves again: undo the version 5 switch-off for existing users
    Object.assign(cached.settings.byKey, {
      canAutoLoadPhotoFromContacts: true,
      canAutoLoadPhotoInPrivateChats: true,
      canAutoLoadPhotoInGroups: true,
      canAutoLoadPhotoInChannels: true,
      canAutoLoadVideoFromContacts: true,
      canAutoLoadVideoInPrivateChats: true,
      canAutoLoadVideoInGroups: true,
      canAutoLoadVideoInChannels: true,
    } satisfies Partial<typeof cached.settings.byKey>);
  }

  if (!cached.auth) {
    cached.auth = initialState.auth;
    cached.auth.rememberMe = untypedCached.rememberMe;
  }

  if (cached.audioPlayer.volume === undefined) {
    cached.audioPlayer.volume = initialState.audioPlayer.volume;
  }

  // Older cached state can have a `customEmojis` entry missing `byId` (a shape added later without
  // a migration), which crashes every reader of it (e.g. `Object.keys(global.customEmojis.byId)`).
  if (!cached.customEmojis?.byId) {
    cached.customEmojis = initialState.customEmojis;
  }
}

function updateCache(force?: boolean) {
  const global = getGlobal();
  if (isRemovingCache || !isCaching || global.auth.isLoggingOut || (!force && getIsHeavyAnimating())) {
    return;
  }

  forceUpdateCache();
}

export function temporarilySuspendCacheUpdate() {
  cacheUpdateSuspensionTimestamp = Date.now() + UPDATE_THROTTLE;
}

export function forceUpdateCache(noEncrypt = false) {
  if (Date.now() < cacheUpdateSuspensionTimestamp) {
    return;
  }

  const global = getGlobal();
  const { hasPasscode, isScreenLocked } = global.passcode;

  if (hasPasscode) {
    if (!isScreenLocked && !noEncrypt) {
      const serializedGlobal = serializeGlobal(global);
      void encryptSession(undefined, serializedGlobal);
    }

    cacheIsScreenLocked(global);
    cacheGlobal(clearGlobalForLockScreen(global, false));
    cacheSharedState(clearSharedStateForLockScreen(global.sharedState));
    // That was a different (locked) payload, so the signature below no longer describes what is on
    // disk — force the next unlocked tick to write
    lastGlobalSignature = undefined;
    lastSharedState = undefined;
    return;
  }

  cacheIsScreenLocked(global);

  // Skip the write when nothing that ends up in the blob has changed. A chat app's state ticks
  // constantly — presence, connection, typing, tab scroll — and this used to serialize and write the
  // whole reduced global (measured ~900 KB) on every one of them: an idle tab was writing to disk
  // roughly 750 times an hour for state that had not moved.
  const signature = buildGlobalSignature(global);
  if (!lastGlobalSignature || !isSameGlobalSignature(lastGlobalSignature, signature)) {
    lastGlobalSignature = signature;
    cacheGlobal(reduceGlobal(global));
  }

  // Shared state (theme, language, per-device settings) changes only when the user changes a setting,
  // and `reduceSharedState` is a pure function of this one slice — so the reference answers it exactly
  if (lastSharedState !== global.sharedState) {
    lastSharedState = global.sharedState;
    cacheSharedState(reduceSharedState(global.sharedState));
  }

  void persistChatMessages(global);
}

// The inputs `reduceGlobal` actually reads. Global state is immutable, so an unchanged reference means
// that slice produced the same output — with one nuance: the users/chats/messages reducers narrow
// their output by what each tab currently has open, so the navigation those tabs are on is part of the
// signature too. Anything not listed here cannot influence what gets persisted.
const PERSISTED_SLICE_KEYS = [
  'cacheVersion', 'appConfig', 'config', 'auth', 'attachMenu', 'currentUserId', 'contactList',
  'topPeerCategories', 'recentEmojis', 'recentCustomEmojis', 'downloadedMedia', 'push',
  'serviceNotifications', 'attachmentSettings', 'leftColumnWidth', 'archiveSettings', 'mediaViewer',
  'audioPlayer', 'shouldShowContextMenuHint', 'trustedBotIds', 'recentlyFoundChatIds', 'peerColors',
  'savedReactionTags', 'timezones', 'availableEffectById', 'lastIsChatInfoShown', 'stickers',
  'customEmojis', 'users', 'chats', 'messages', 'settings', 'chatFolders', 'groupCalls', 'reactions',
  'passcode',
] as const satisfies readonly (keyof GlobalState)[];

let lastGlobalSignature: unknown[] | undefined;
let lastSharedState: GlobalState['sharedState'] | undefined;

function buildGlobalSignature(global: GlobalState): unknown[] {
  const signature: unknown[] = PERSISTED_SLICE_KEYS.map((key) => global[key]);

  Object.values(global.byTabId).forEach((tab) => {
    const messageList = selectCurrentMessageList(global, tab.id);
    signature.push(tab.id, messageList?.chatId, messageList?.threadId, tab.forumPanelChatId);
  });

  return signature;
}

function isSameGlobalSignature(previous: unknown[], next: unknown[]) {
  return previous.length === next.length && previous.every((value, index) => value === next[index]);
}

// Chat history is deliberately NOT part of the reduced global. That blob is rewritten in full on
// every cache tick, so anything kept in it has to stay small — which is why only the open chat's
// on-screen slice survived a reload (measured: 49 chats cached, 16 of them holding more than a single
// message). Writing history per chat instead lets each one be saved on its own, and only when it has
// actually changed, so the amount kept is no longer tied to the cost of the global write.
async function persistChatMessages(global: GlobalState) {
  const chatIds = (getOrderedIds(ALL_FOLDER_ID) || []).slice(0, MESSAGE_STORE_CHAT_LIMIT);
  let hasWritten = false;

  for (const chatId of chatIds) {
    const chatMessages = global.messages.byChatId[chatId];
    if (!chatMessages) continue;

    const listedIds = chatMessages.threadsById[MAIN_THREAD_ID]?.localState?.listedIds;
    if (!listedIds?.length) continue;

    const ids = listedIds.slice(-MESSAGE_STORE_MESSAGE_LIMIT)
      .filter((id) => chatMessages.byId[id] && !chatMessages.byId[id].isTypingDraft);

    // Compare what will actually be written, not the whole chat slice. Global state is immutable, so
    // an unchanged message keeps its reference and this is exact — but the slice's reference changes
    // for things the persisted payload does not contain, a typing indicator above all (it enters
    // `byId` as a draft and is filtered out below). Keying the check on the slice therefore rewrote
    // the full 200-message blob of every chat someone was typing in, on every 5-second tick.
    const previous = lastPersistedByChatId.get(chatId);
    if (previous && isSamePersistedPayload(previous, ids, chatMessages.byId)) continue;

    const byId: Record<number, ApiMessage> = {};
    const sourceById: Record<number, ApiMessage> = {};
    ids.forEach((id) => {
      sourceById[id] = chatMessages.byId[id];
      byId[id] = omitLocalPaidReactions(omitLocalMedia(chatMessages.byId[id]));
    });

    if (!ids.length) {
      lastPersistedByChatId.set(chatId, { ids, byId: sourceById });
      continue;
    }

    try {
      await saveChatMessages(chatId, { ids, byId });
      // Only after the write lands. Recording it first meant a rejected write (a full disk — the very
      // case this pass exists to survive) left the snapshot claiming that content was persisted, so the
      // dirty check skipped it from then on and the chat silently stopped being saved.
      lastPersistedByChatId.set(chatId, { ids, byId: sourceById });
      hasWritten = true;
    } catch {
      // One chat failing must not abort the loop, or every chat after it is skipped and the prune below
      // never runs
    }
  }

  if (!hasWritten) return;

  lastPersistedByChatId.forEach((_, chatId) => {
    if (!chatIds.includes(chatId)) lastPersistedByChatId.delete(chatId);
  });

  await pruneMessageStore(chatIds);
}

function reduceGlobal<T extends GlobalState>(global: T) {
  const reducedGlobal: GlobalState = {
    ...INITIAL_GLOBAL_STATE,
    ...pick(global, [
      'cacheVersion',
      'appConfig',
      'config',
      'auth',
      'attachMenu',
      'currentUserId',
      'contactList',
      'topPeerCategories',
      'recentEmojis',
      'recentCustomEmojis',
      'downloadedMedia',
      'push',
      'serviceNotifications',
      'attachmentSettings',
      'leftColumnWidth',
      'archiveSettings',
      'mediaViewer',
      'audioPlayer',
      'shouldShowContextMenuHint',
      'trustedBotIds',
      'recentlyFoundChatIds',
      'peerColors',
      'savedReactionTags',
      'timezones',
      'availableEffectById',
    ]),
    lastIsChatInfoShown: !getIsMobile() ? global.lastIsChatInfoShown : undefined,
    stickers: reduceStickers(global),
    customEmojis: reduceCustomEmojis(global),
    users: reduceUsers(global),
    chats: reduceChats(global),
    messages: reduceMessages(global),
    settings: reduceSettings(global),
    chatFolders: reduceChatFolders(global),
    groupCalls: reduceGroupCalls(global),
    reactions: {
      ...pick(global.reactions, [
        'defaultTags',
        'recentReactions',
        'topReactions',
        'effectReactions',
        'hash',
      ]),
      availableReactions: reduceAvailableReactions(global.reactions.availableReactions),
    },
    passcode: pick(global.passcode, [
      'isScreenLocked',
      'hasPasscode',
      'invalidAttemptsCount',
      'timeoutUntil',
    ]),
  };

  return reducedGlobal;
}

function reduceSharedState(sharedState: SharedState): SharedState {
  return {
    ...sharedState,
    settings: {
      ...sharedState.settings,
      languages: undefined,
    },
    isInitial: undefined,
  };
}

export function serializeGlobal<T extends GlobalState>(global: T) {
  return JSON.stringify(reduceGlobal(global));
}

function reduceStickers<T extends GlobalState>(global: T): GlobalState['stickers'] {
  const {
    added, recent, favorite, diceSetIdByEmoji, setsById: allSetsById,
  } = global.stickers;

  // Persist the installed ("added") set list + its loaded metadata (and recent/favorite) so a reload
  // shows stickers instantly from cache instead of blanking on "No stickers yet" until a re-fetch
  // succeeds. The media blobs themselves are cached separately in the media cache. Dice sets are kept
  // too (needed to roll dice messages before their set is otherwise loaded).
  const setIdsToKeep = unique([
    ...Object.values(diceSetIdByEmoji || {}),
    ...(added.setIds || []),
  ]);

  // Metadata for every one of those, but sticker lists only for the first few. Browsing the picker
  // fills `setsById` with one array per set visited, and persisting all of them means the whole
  // browsed catalogue is re-serialized on every flush. Dice sets keep theirs regardless — a dice
  // message has to roll before its set would otherwise be fetched.
  // Normalized to strings: the loop below matches against `Object.keys(setsById)`, which is always a
  // string, while the backend hands set ids back as numbers. Unnormalized, the lookup never matched
  // and every set lost its stickers on flush — so a reload re-fetched the whole catalogue.
  const setIdsKeepingStickers = new Set([
    ...Object.values(diceSetIdByEmoji || {}),
    ...(added.setIds || []).slice(0, GLOBAL_STATE_CACHE_STICKER_SET_LIMIT),
  ].map(String));
  const setsById = pick(allSetsById, setIdsToKeep);
  for (const id of Object.keys(setsById)) {
    const set = setIdsKeepingStickers.has(id) || !setsById[id].stickers
      ? setsById[id]
      : omit(setsById[id], ['stickers']);
    setsById[id] = omitStickerSetThumbnails(set);
  }

  return {
    ...INITIAL_GLOBAL_STATE.stickers,
    added,
    recent,
    favorite,
    diceSetIdByEmoji,
    setsById,
  };
}

// A sticker's `thumbnail.dataUri` is a base64 blur preview, and it is by far the largest thing in
// the persisted state: measured on a real account, 279 KB of an 899 KB cache spread over 1106
// sticker objects inside `packs`. It is also redundant — the sticker's own file is in the media
// cache, which returns it in under a millisecond, so the preview it stands in for is never seen.
// `recent`/`favorite` keep theirs: those render first on a cold open, before any set is fetched.
function omitStickerSetThumbnails(set: ApiStickerSet): ApiStickerSet {
  const { stickers, packs, covers } = set;
  if (!stickers && !packs && !covers) return set;

  const reduced = { ...set };

  if (stickers) reduced.stickers = stickers.map(omitStickerThumbnail);
  if (covers) reduced.covers = covers.map(omitStickerThumbnail);
  if (packs) {
    reduced.packs = Object.keys(packs).reduce((acc, emoji) => {
      acc[emoji] = packs[emoji].map(omitStickerThumbnail);
      return acc;
    }, {} as Record<string, ApiSticker[]>);
  }

  return reduced;
}

function omitStickerThumbnail(sticker: ApiSticker): ApiSticker {
  if (!sticker.thumbnail) return sticker;

  return {
    ...sticker,
    thumbnail: undefined,
  };
}

function reduceCustomEmojis<T extends GlobalState>(global: T): GlobalState['customEmojis'] {
  const { lastRendered, byId } = global.customEmojis;
  const folderEmojiIds = Object.values(global.chatFolders.byId)
    .flatMap((folder) => (
      folder.title.entities
        ?.filter((entity) => entity.type === ApiMessageEntityTypes.CustomEmoji)
        ?.map((entity) => entity.documentId) || []
    ));
  const idsToSave = unique([...folderEmojiIds, ...lastRendered]).slice(0, GLOBAL_STATE_CACHE_CUSTOM_EMOJI_LIMIT);
  const byIdToSave = pick(byId, idsToSave);

  return {
    byId: byIdToSave,
    lastRendered: idsToSave,
    forEmoji: {},
    added: {},
    statusRecent: {},
  };
}

function reduceUsers<T extends GlobalState>(global: T): GlobalState['users'] {
  const {
    users: {
      byId, statusesById, fullInfoById, botAppPermissionsById,
    }, currentUserId,
  } = global;
  const currentChatIds = compact(
    Object.values(global.byTabId)
      .map(({ id: tabId }) => selectCurrentMessageList(global, tabId)),
  ).map(({ chatId }) => chatId).filter((chatId) => isUserId(chatId));

  const visibleUserIds = unique(compact(Object.values(global.byTabId)
    .flatMap(({ id: tabId }) => selectVisibleUsers(global, tabId)?.map((u) => u.id) || [])));

  const chatStoriesUserIds = currentChatIds
    .flatMap((chatId) => Object.values(selectChatMessages(global, chatId) || {}))
    .map((message) => {
      const webPage = selectFullWebPageFromMessage(global, message);
      return message.content.storyData?.peerId || webPage?.story?.peerId;
    })
    .filter((id): id is string => Boolean(id) && isUserId(id));

  const attachBotIds = Object.keys(global.attachMenu?.bots || {});
  const topPeerIds = getTopPeerIds(global);

  const idsToSave = unique([
    ...currentUserId ? [currentUserId] : [],
    ...currentChatIds,
    ...chatStoriesUserIds,
    ...visibleUserIds || [],
    ...attachBotIds,
    ...topPeerIds.filter(isUserId),
    ...global.recentlyFoundChatIds?.filter(isUserId) || [],
    ...getOrderedIds(ARCHIVED_FOLDER_ID)?.slice(0, GLOBAL_STATE_CACHE_ARCHIVED_CHAT_LIST_LIMIT).filter(isUserId) || [],
    ...getOrderedIds(ALL_FOLDER_ID)?.filter(isUserId) || [],
    ...global.contactList?.userIds || [],
    ...Object.keys(byId),
  ]).slice(0, GLOBAL_STATE_CACHE_USER_LIST_LIMIT);

  return {
    ...INITIAL_GLOBAL_STATE.users,
    byId: pickTruthy(byId, idsToSave),
    statusesById: pickTruthy(statusesById, idsToSave),
    fullInfoById: pickTruthy(fullInfoById, idsToSave),
    botAppPermissionsById,
  };
}

function reduceChats<T extends GlobalState>(global: T): GlobalState['chats'] {
  const { chats: { byId }, currentUserId } = global;
  const currentChatIds = compact(
    Object.values(global.byTabId)
      .map(({ id: tabId }): MessageList | undefined => {
        return selectCurrentMessageList(global, tabId);
      }),
  ).map(({ chatId }) => chatId);

  const messagesChatIds = compact(Object.values(global.byTabId).flatMap(({ id: tabId }) => {
    const messageList = selectCurrentMessageList(global, tabId);
    if (!messageList) return undefined;

    const messages = selectChatMessages(global, messageList.chatId);
    const viewportIds = selectViewportIds(global, messageList.chatId, messageList.threadId, tabId);
    return viewportIds?.map((id) => {
      const message = messages[id];
      if (!message) return undefined;
      const content = message.content;
      const webPage = selectFullWebPageFromMessage(global, message);
      const replyPeer = message.replyInfo?.type === 'message' && message.replyInfo.replyToPeerId;
      return content.storyData?.peerId || webPage?.story?.peerId || replyPeer;
    });
  }));
  const topPeerIds = getTopPeerIds(global);

  const unlinkedIdsToSave = [
    ...currentUserId ? [currentUserId] : [],
    ...currentChatIds,
    ...messagesChatIds,
    ...topPeerIds,
    ...global.recentlyFoundChatIds || [],
    ...getOrderedIds(ARCHIVED_FOLDER_ID)?.slice(0, GLOBAL_STATE_CACHE_ARCHIVED_CHAT_LIST_LIMIT) || [],
    ...getOrderedIds(ALL_FOLDER_ID) || [],
    ...getOrderedIds(SAVED_FOLDER_ID) || [],
    ...Object.keys(byId),
  ];

  let idsToSave: string[] = [];

  for (const id of unlinkedIdsToSave) {
    const chat = byId[id];
    if (!chat) continue;

    idsToSave.push(id);

    if (chat.linkedMonoforumId) {
      idsToSave.push(chat.linkedMonoforumId);
    }
  }

  idsToSave = unique(idsToSave).slice(0, GLOBAL_STATE_CACHE_CHAT_LIST_LIMIT);

  return {
    ...global.chats,
    similarChannelsById: {},
    similarBotsById: {},
    isFullyLoaded: {},
    // `|| {}` guards each map: a state rehydrated from an older cache can lack one of these
    // sub-objects, and `pickTruthy(undefined, …)` throws (`undefined is not an object`), crashing
    // the whole cache write. An empty map is the correct default for the missing slice.
    notifyExceptionById: pickTruthy(global.chats.notifyExceptionById || {}, idsToSave),
    loadingParameters: INITIAL_GLOBAL_STATE.chats.loadingParameters,
    byId: omitChatCallFlags(pickTruthy(global.chats.byId || {}, idsToSave)),
    fullInfoById: pickTruthy(global.chats.fullInfoById || {}, idsToSave),
    lastMessageIds: {
      all: pickTruthy(global.chats.lastMessageIds?.all || {}, idsToSave),
      saved: global.chats.lastMessageIds?.saved,
    },
    topicsInfoById: reduceTopicsInfo(global.chats.topicsInfoById || {}, currentChatIds),
  };
}

// `isCallActive`/`isCallNotEmpty` describe a group call that is happening RIGHT NOW. Nothing ever
// rebuilds them from the server — the chat payload carries no call flags, so they are only set by
// the `updateGroupCallChatId` update. Persisting them means a client that left a call before it was
// discarded (and so never received the discard update) keeps `isCallActive: true` forever: every
// reload restores it from cache and the chat keeps offering a "Join video chat" that does nothing.
// Drop them on the way into the cache so each session re-derives live call state from updates.
function omitChatCallFlags(byId: GlobalState['chats']['byId']): GlobalState['chats']['byId'] {
  const result: GlobalState['chats']['byId'] = {};

  for (const [chatId, chat] of Object.entries(byId)) {
    result[chatId] = chat.isCallActive || chat.isCallNotEmpty
      ? omit(chat, ['isCallActive', 'isCallNotEmpty'])
      : chat;
  }

  return result;
}

function reduceTopicsInfo(
  topicsInfoById: Record<string, TopicsInfo>, chatIds: string[],
): GlobalState['chats']['topicsInfoById'] {
  const topicsInfoToSave = pickTruthy(topicsInfoById, chatIds);

  return Object.entries(topicsInfoToSave).reduce((acc, [chatId, topicsInfo]) => {
    acc[chatId] = {
      ...topicsInfo,
      isCache: true,
    };

    return acc;
  }, {} as GlobalState['chats']['topicsInfoById']);
}

function getTopPeerIds<T extends GlobalState>(global: T) {
  return unique(Object.values(global.topPeerCategories).flatMap((category) => category?.peerIds || []));
}

function reduceMessages<T extends GlobalState>(global: T): GlobalState['messages'] {
  const { currentUserId } = global;
  const byChatId: GlobalState['messages']['byChatId'] = {};
  const currentChatIds = compact(
    Object.values(global.byTabId)
      .map(({ id: tabId }) => selectCurrentMessageList(global, tabId)),
  ).map(({ chatId }) => chatId);
  const forumPanelChatIds = compact(
    Object.values(global.byTabId)
      .map(({ forumPanelChatId }) => forumPanelChatId),
  );
  const chatIdsToSave = unique([
    ...currentChatIds,
    ...currentUserId ? [currentUserId] : [],
    ...forumPanelChatIds,
    ...getOrderedIds(ALL_FOLDER_ID) || [],
    ...getOrderedIds(ARCHIVED_FOLDER_ID)?.slice(0, GLOBAL_STATE_CACHE_ARCHIVED_CHAT_LIST_LIMIT) || [],
  ]);

  const openedChatThreadIds = Object.values(global.byTabId).reduce((acc, { id: tabId }) => {
    const { chatId: tabChatId, threadId } = selectCurrentMessageList(global, tabId) || {};
    if (!tabChatId || !threadId || threadId === MAIN_THREAD_ID) {
      return acc;
    }
    const current = acc[tabChatId] || new Set();
    current.add(threadId);
    acc[tabChatId] = current;

    return acc;
  }, {} as Record<string, Set<ThreadId>>);

  const pollIdsToSave: string[] = [];
  const webPageIdsToSave: string[] = [];

  chatIdsToSave.forEach((chatId) => {
    const current = global.messages.byChatId[chatId];
    if (!current) {
      return;
    }

    const chatLastMessageId = selectChatLastMessageId(global, chatId);

    const topicsInfo = selectTopicsInfo(global, chatId);
    const openedThreadIds = Array.from(openedChatThreadIds[chatId] || []);
    const commentThreadIds = Object.values(global.messages.byChatId[chatId].threadsById || {})
      .map(({ threadInfo }) => (threadInfo?.isCommentsInfo ? threadInfo?.originMessageId : undefined))
      .filter(Boolean);
    const threadIds = unique(openedThreadIds.concat(commentThreadIds, topicsInfo?.listedTopicIds || []));

    const topics = selectTopics(global, chatId);
    const threadsToSave = pickTruthy(current.threadsById, [MAIN_THREAD_ID, ...threadIds]);

    const viewportIdsToSave = unique(Object.values(threadsToSave)
      .flatMap((thread) => thread.localState?.lastViewportIds || []));
    const topicLastMessageIds = topics && forumPanelChatIds.includes(chatId)
      ? Object.values(topics).map(({ id }) => selectThreadInfo(global, chatId, id)?.lastMessageId).filter(Boolean) : [];
    const savedLastMessageIds = chatId === currentUserId && global.chats.lastMessageIds.saved
      ? Object.values(global.chats.lastMessageIds.saved) : [];
    const lastMessageIdsToSave = [chatLastMessageId].concat(topicLastMessageIds).concat(savedLastMessageIds)
      .filter(Boolean);
    const byId = pick(current.byId, viewportIdsToSave.concat(lastMessageIdsToSave));
    const threadsById = Object.keys(threadsToSave).reduce((acc, key) => {
      const thread = threadsToSave[Number(key)];
      acc[Number(key)] = {
        ...thread,
        localState: {
          ...thread.localState,
          listedIds: thread.localState?.lastViewportIds,
          typingStatusByPeerId: undefined,
        },
      };
      return acc;
    }, {} as GlobalState['messages']['byChatId'][string]['threadsById']);

    const cleanedById = Object.values(byId).reduce((acc, message) => {
      if (!message || message.isTypingDraft) return acc;

      let cleanedMessage = omitLocalMedia(message);
      cleanedMessage = omitLocalPaidReactions(cleanedMessage);
      acc[message.id] = cleanedMessage;

      if (message.content.pollId) {
        pollIdsToSave.push(message.content.pollId);
      }

      if (message.content.webPage) {
        webPageIdsToSave.push(message.content.webPage.id);
      }

      return acc;
    }, {} as Record<number, ApiMessage>);

    byChatId[chatId] = {
      byId: cleanedById,
      threadsById,
      summaryById: {},
    };
  });

  return {
    byChatId,
    pollById: pickTruthy(global.messages.pollById, pollIdsToSave),
    webPageById: pickTruthy(global.messages.webPageById, webPageIdsToSave),
    sponsoredByChatId: {},
    playbackByChatId: {},
  };
}

function omitLocalPaidReactions(message: ApiMessage): ApiMessage {
  if (!message.reactions?.results.length) return message;
  return {
    ...message,
    reactions: {
      ...message.reactions,
      results: message.reactions.results.map((reaction) => {
        if (reaction.localAmount) {
          return {
            ...reaction,
            localAmount: undefined,
          };
        }
        return reaction;
      }),
    },
  };
}

// A local message's blur thumbnail points at an object URL (`blob:`), which dies on reload — drop it
// so a restored failed message doesn't try to load a dead blob. Server thumbnails are `data:` base64
// and are kept. `ApiThumbnail.dataUri` is required, so clear the whole (optional) `thumbnail`.
function omitLocalThumbnail(thumbnail?: ApiThumbnail) {
  return thumbnail?.dataUri.startsWith('blob:') ? undefined : thumbnail;
}

function omitLocalMedia(message: ApiMessage): ApiMessage {
  const {
    photo, video, document, sticker,
  } = message.content;

  return {
    ...message,
    content: {
      ...message.content,
      photo: photo && {
        ...photo,
        blobUrl: undefined,
        thumbnail: omitLocalThumbnail(photo.thumbnail),
      },
      video: video && {
        ...video,
        blobUrl: undefined,
        previewBlobUrl: undefined,
        thumbnail: omitLocalThumbnail(video.thumbnail),
      },
      document: document && {
        ...document,
        previewBlobUrl: undefined,
      },
      sticker: sticker && {
        ...sticker,
        isPreloadedGlobally: undefined,
      },
    },
    previousLocalId: undefined,
    // The in-session resend buffer dies with the worker, so a reloaded failed message can't be resent.
    // Keep the failed `sendingState` (the placeholder stays visible) but drop the resend affordance.
    isResendable: undefined,
    // A message still in flight when the page went away can never complete — its worker send died with
    // the page. Persisting it as pending brought it back as a bubble with a spinner that spins forever
    // and offers no way out; in any chat other than the one open at sync time it stayed that way
    // indefinitely, re-persisted on every tick. Record what is actually true instead: it failed.
    sendingState: message.sendingState === 'messageSendingStatePending'
      ? 'messageSendingStateFailed'
      : message.sendingState,
  };
}

function reduceSettings<T extends GlobalState>(global: T): GlobalState['settings'] {
  const {
    byKey, botVerificationShownPeerIds, notifyDefaults, lastPremiumBandwithNotificationDate, themes, accountDaysTtl,
  } = global.settings;

  return {
    byKey,
    privacy: {},
    botVerificationShownPeerIds,
    lastPremiumBandwithNotificationDate,
    notifyDefaults,
    themes,
    accountDaysTtl,
  };
}

function reduceChatFolders<T extends GlobalState>(global: T): GlobalState['chatFolders'] {
  return {
    ...global.chatFolders,
  };
}

function reduceGroupCalls<T extends GlobalState>(global: T): GlobalState['groupCalls'] {
  return {
    ...global.groupCalls,
    byId: {},
    activeGroupCallId: undefined,
  };
}

function reduceAvailableReactions(availableReactions?: ApiAvailableReaction[]): ApiAvailableReaction[] | undefined {
  return availableReactions
    ?.map((r) => ({ ...pick(r, ['reaction', 'staticIcon', 'title', 'isInactive']), isLocalCache: true }));
}
